import { Extension } from '@tiptap/core';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { Plugin } from '@tiptap/pm/state';
import type { EnrichedEntity } from '@/lib/types';

export interface EntityMention { entity: EnrichedEntity; start: number; end: number; label: string }

const escape = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export function buildEntityMentions(text: string, entities: EnrichedEntity[]): EntityMention[] {
  const names = new Map<string, EnrichedEntity[]>();
  entities.forEach((entity) => {
    const character = entity as EnrichedEntity & { fullName?: string; nickname?: string };
    const values = [entity.name, entity.entityType === 'characters' ? character.fullName : undefined, entity.entityType === 'characters' ? character.nickname : undefined]
      .filter((value): value is string => Boolean(value?.trim()));
    [...new Set(values)].forEach((value) => {
      const key = value.normalize('NFC').toLocaleLowerCase();
      names.set(key, [...(names.get(key) || []), entity]);
    });
  });
  const pattern = [...names.keys()].sort((a, b) => b.length - a.length).map(escape).join('|');
  if (!pattern) return [];
  const result: EntityMention[] = [];
  const regex = new RegExp(`(?<![\\p{L}\\p{N}_])(${pattern})(?![\\p{L}\\p{N}_])`, 'giu');
  for (const match of text.matchAll(regex)) {
    const label = match[0];
    const key = label.normalize('NFC').toLocaleLowerCase();
    const start = match.index || 0;
    for (const entity of names.get(key) || []) result.push({ entity, start, end: start + label.length, label });
  }
  return result;
}

function buildDecorations(doc: any, entities: EnrichedEntity[]) {
  const decorations: Decoration[] = [];
  doc.descendants((node: any, pos: number) => {
    if (!node.isText || !node.text) return;
    buildEntityMentions(node.text, entities).forEach((mention) => {
      const type = mention.entity.entityType;
      const color = type === 'characters' ? '#078c95' : type === 'locations' ? '#4b8f54' : '#7654a6';
      decorations.push(Decoration.inline(pos - 1 + mention.start, pos + mention.end, {
        class: `entity-mention entity-mention-${type}`,
        style: `color:${color};font-weight:400;text-decoration:none;cursor:help`,
        'data-entity-id': mention.entity.id,
        'data-entity-type': type,
      }));
    });
  });
  return DecorationSet.create(doc, decorations);
}

export const EntityHighlights = Extension.create<{ enabled: boolean; entities: EnrichedEntity[] }>({
  name: 'entityHighlights',
  addOptions: () => ({ enabled: false, entities: [] }),
  addProseMirrorPlugins() {
    const extension = this;
    return [new Plugin({
      props: {
        decorations: (state) => extension.options.enabled
          ? buildDecorations(state.doc, extension.options.entities)
          : DecorationSet.empty,
      },
    })];
  },
});
